import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, 
  Calendar as CalendarIcon, 
  Calendar,
  Clock, 
  DollarSign,
  TrendingUp,
  UserPlus,
  AlertCircle,
  CheckCircle,
  PlayCircle,
  XCircle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileText,
  CalendarDays,
  UserX,
  X,
  Stethoscope,
  User,
  MessageSquare,
  FileWarning,
  AlertTriangle,
  Bell,
  Fingerprint,
  Smartphone,
  Loader2,
  Search
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseLocalDateTime, formatDateTime } from '../utils/dateUtils';
import { BRAND_COLORS, getProfileColor, TYPOGRAPHY } from '../utils/designSystem';
import { ContratoPendenteModal } from '../components/ContratoPendenteModal';
import PushNotificationSettings from '../components/PushNotificationSettings';
import { usePushNotifications } from '../hooks/usePushNotifications';

// Elegant StatCard with soft colors
const StatCard = ({ title, value, icon: Icon, colorKey = 'green', onClick, subtitle }) => {
  const color = BRAND_COLORS[colorKey] || BRAND_COLORS.green;
  
  return (
    <div
      onClick={onClick}
      className="group relative overflow-hidden rounded-2xl border p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
      style={{ 
        backgroundColor: color.bg,
        borderColor: color.border,
      }}
    >
      {/* Subtle gradient overlay */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ 
          background: `linear-gradient(135deg, ${color.light} 0%, transparent 60%)` 
        }}
      />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div 
            className="p-2.5 rounded-xl transition-colors duration-300"
            style={{ backgroundColor: color.bgHover }}
          >
            <Icon className="h-5 w-5" style={{ color: color.text }} />
          </div>
          <ChevronRight 
            className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1" 
            style={{ color: color.text }} 
          />
        </div>
        <p 
          className="text-2xl font-semibold tracking-tight"
          style={{ color: color.text, fontFamily: 'Outfit, sans-serif' }}
        >
          {value}
        </p>
        <p 
          className="text-sm mt-1"
          style={{ color: color.text, opacity: 0.8 }}
        >
          {title}
        </p>
        {subtitle && (
          <p className="text-xs mt-1" style={{ color: color.text, opacity: 0.6 }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

const DashboardHome = () => {
  const { user, authFetch, isAdmin, isRecepcao, isAdminOrRecepcao, isProfissional, registerBiometric, getBiometricCredentials } = useAuth();
  const navigate = useNavigate();
  // Estado do push notification — usado para alertar Profissional se não estiver ativo
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    toggleSubscription: togglePushSubscription,
    isIOS: pushIsIOS,
    isStandalone: pushIsStandalone,
  } = usePushNotifications();
  const [stats, setStats] = useState(null);
  const [agendamentosHoje, setAgendamentosHoje] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensagensNaoLidas, setMensagensNaoLidas] = useState(0);
  const [pacientesAguardando, setPacientesAguardando] = useState([]);
  const [faltasCancelamentos, setFaltasCancelamentos] = useState([]);
  
  // Alertas do Profissional
  const [alertasStatusIncoerente, setAlertasStatusIncoerente] = useState({ total: 0, pacientes: [] });
  const [alertasSemEvolucao, setAlertasSemEvolucao] = useState({ total: 0, pacientes: [] });
  
  // Estado para Equipe Ativa (Recepção)
  const [equipeAtiva, setEquipeAtiva] = useState(null);
  const [equipeAtivaExpanded, setEquipeAtivaExpanded] = useState(false);
  const [loadingEquipe, setLoadingEquipe] = useState(false);
  const [buscaEquipe, setBuscaEquipe] = useState('');
  // 2026-02 — Set com IDs de profissionais que estão expandidos (colapsador de convênios)
  const [profConveniosExpandidos, setProfConveniosExpandidos] = useState(new Set());
  const toggleConveniosProf = (profId) => {
    setProfConveniosExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(profId)) next.delete(profId);
      else next.add(profId);
      return next;
    });
  };

  // Estado para Cobertura de Push (Admin/Recepção)
  const [coberturaPush, setCoberturaPush] = useState(null);
  const [coberturaPushExpanded, setCoberturaPushExpanded] = useState(false);
  
  // Estados para modais de detalhes
  const [detailsModal, setDetailsModal] = useState({ open: false, type: null, title: '', data: [] });
  // Anti double-click no botão "Iniciar Atendimento" (evita disparos duplicados
  // enquanto o PUT /status está em curso — reportado por Anna Beatriz em 2026-08).
  const [iniciandoIds, setIniciandoIds] = useState(new Set());
  
  // Estado para modal de biometria
  const [biometricModal, setBiometricModal] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  
  // Redirecionamento do Admin para Inteligência Gerencial (sua página inicial)
  // Usando useEffect para evitar race condition com o contexto de autenticação
  useEffect(() => {
    if (user?.perfil === 'administrador') {
      // Pequeno delay para garantir que o contexto está totalmente carregado
      const timer = setTimeout(() => {
        navigate('/dashboard/relatorios', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user?.perfil, navigate]);
  
  // Verificar se WebAuthn está disponível e se o usuário já tem biometria
  useEffect(() => {
    const checkBiometric = async () => {
      console.log('Checking WebAuthn availability...');
      
      if (!window.PublicKeyCredential) {
        console.log('WebAuthn: PublicKeyCredential not available');
        return;
      }
      
      // Verificar se é um dispositivo móvel (touch screen)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || ('ontouchstart' in window) 
        || (navigator.maxTouchPoints > 0);
      
      console.log('Is mobile device:', isMobile);
      
      if (!isMobile) {
        console.log('WebAuthn: Skipping biometric prompt on desktop');
        return;
      }
      
      try {
        const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        console.log('WebAuthn available:', available);
        setBiometricAvailable(available);
        
        if (available && user?.id) {
          // Verificar se já tem biometria cadastrada
          const creds = await getBiometricCredentials();
          console.log('Existing credentials:', creds.length);
          
          const hasShownBefore = localStorage.getItem(`biometric_asked_${user.id}`);
          console.log('Has shown before:', hasShownBefore);
          
          // Só mostrar modal se:
          // 1. Não tem biometria cadastrada
          // 2. Nunca perguntamos antes (null) - NÃO mostrar se já foi 'registered' ou 'dismissed'
          if (creds.length === 0 && !hasShownBefore) {
            console.log('Will show biometric modal in 3 seconds...');
            setTimeout(() => {
              console.log('Showing biometric modal now');
              setBiometricModal(true);
            }, 3000);
          } else {
            console.log('Skipping biometric modal - already asked or registered');
          }
        }
      } catch (e) {
        console.log('WebAuthn check failed:', e);
      }
    };
    
    if (user?.id && !loading) {
      checkBiometric();
    }
  }, [user?.id, loading, getBiometricCredentials]);
  
  // Função para registrar biometria
  const handleRegisterBiometric = async () => {
    setBiometricLoading(true);
    try {
      const result = await registerBiometric('Meu Dispositivo');
      if (result.success) {
        toast.success('Biometria cadastrada com sucesso! 🎉');
        setBiometricModal(false);
        localStorage.setItem(`biometric_asked_${user.id}`, 'registered');
      } else {
        toast.error(result.error || 'Erro ao cadastrar biometria');
      }
    } catch (err) {
      toast.error('Erro ao cadastrar biometria');
    } finally {
      setBiometricLoading(false);
    }
  };
  
  // Função para não perguntar novamente
  const handleDismissBiometric = () => {
    localStorage.setItem(`biometric_asked_${user.id}`, 'dismissed');
    setBiometricModal(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      // Só buscar dados quando o user estiver com perfil definido
      if (!user?.perfil) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      try {
        // PROFISSIONAL: Usar endpoint consolidado (1 chamada ao invés de 6)
        if (user.perfil === 'profissional') {
          const consolidadoRes = await authFetch('/dashboard/profissional-consolidado');
          const data = consolidadoRes.data || consolidadoRes;
          
          setStats(data.stats || {});
          setAgendamentosHoje(data.agendamentos_hoje || []);
          setPacientesAguardando(data.pacientes_aguardando || []);
          setFaltasCancelamentos(data.faltas_cancelamentos || []);
          setAlertasStatusIncoerente(data.alertas_status_incoerente || { total: 0, pacientes: [] });
          setAlertasSemEvolucao(data.alertas_sem_evolucao || { total: 0, pacientes: [] });
        } 
        // RECEPÇÃO/ADMIN: Manter lógica atual com Promise.all
        else {
          const [statsRes, agendRes] = await Promise.all([
            authFetch('/dashboard/stats'),
            authFetch('/dashboard/agendamentos-hoje')
          ]);
          
          setStats(statsRes.data);
          setAgendamentosHoje(agendRes.data || []);
          
          if (user.perfil === 'recepcao' || user.perfil === 'administrador') {
            const msgRes = await authFetch('/mural/avisos/nao-lidos', { suppressAuthRedirect: true, timeout: 5000 }).catch(err => {
              console.error('Erro ao buscar mensagens não lidas:', err.message);
              return { data: { nao_lidos: 0 } };
            });
            setMensagensNaoLidas(msgRes.data?.nao_lidos || 0);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.perfil, authFetch]);

  // Auto-refresh a cada 30 segundos para manter dados atualizados
  useEffect(() => {
    if (!user?.perfil) return;
    
    const refreshInterval = setInterval(async () => {
      try {
        // PROFISSIONAL: Usar endpoint consolidado
        if (user.perfil === 'profissional') {
          const consolidadoRes = await authFetch('/dashboard/profissional-consolidado', { suppressAuthRedirect: true });
          const data = consolidadoRes.data || consolidadoRes;
          
          if (data.stats) setStats(data.stats);
          if (data.agendamentos_hoje) setAgendamentosHoje(data.agendamentos_hoje);
          if (data.pacientes_aguardando) setPacientesAguardando(data.pacientes_aguardando);
          if (data.alertas_status_incoerente) setAlertasStatusIncoerente(data.alertas_status_incoerente);
          if (data.alertas_sem_evolucao) setAlertasSemEvolucao(data.alertas_sem_evolucao);
        } 
        // RECEPÇÃO/ADMIN: Manter lógica com Promise.all
        else {
          const basePromises = [
            authFetch('/dashboard/stats', { suppressAuthRedirect: true }),
            authFetch('/dashboard/agendamentos-hoje', { suppressAuthRedirect: true })
          ];

          const results = await Promise.all(basePromises.map(p => p.catch(e => null)));

          if (results[0]?.data) setStats(results[0].data);
          if (results[1]?.data) setAgendamentosHoje(results[1].data || []);
        }
      } catch (err) {
        console.debug('Auto-refresh silencioso:', err.message);
      }
    }, 30000); // 30 segundos
    
    return () => clearInterval(refreshInterval);
  }, [user?.perfil, authFetch]);

  // Função para abrir modal com agendamentos por status
  const openStatusDetails = (status, title) => {
    const filtered = agendamentosHoje.filter(ag => ag.status === status);
    setDetailsModal({
      open: true,
      type: 'status',
      title: title,
      data: filtered
    });
  };

  // Função para abrir modal com terapeutas do dia
  const openTerapeutasDetails = () => {
    // Filtrar agendamentos do dia (exceto cancelados) - mesma lógica do contador
    const agendamentosDoDia = agendamentosHoje.filter(ag => ag.status !== 'cancelado');
    
    // Agrupar por profissional
    const terapeutasMap = {};
    agendamentosDoDia.forEach(ag => {
      const profId = ag.profissional_id;
      if (!profId) return;
      const profNome = ag.profissionais?.usuarios?.nome || 'Profissional';
      if (!terapeutasMap[profId]) {
        terapeutasMap[profId] = {
          nome: profNome,
          especialidade: ag.profissionais?.especialidade || '',
          agendamentos: []
        };
      }
      terapeutasMap[profId].agendamentos.push(ag);
    });
    
    setDetailsModal({
      open: true,
      type: 'terapeutas',
      title: 'Terapeutas com Agenda Hoje',
      data: Object.values(terapeutasMap)
    });
  };

  // Função para abrir modal com pacientes restantes
  const openPacientesRestantes = () => {
    const restantes = agendamentosHoje.filter(ag => 
      ['agendado', 'aguardando'].includes(ag.status)
    );
    setDetailsModal({
      open: true,
      type: 'pacientes',
      title: 'Pacientes Restantes Hoje',
      data: restantes
    });
  };

  const getPerfilLabel = (perfil) => {
    const labels = {
      administrador: 'Administrador',
      recepcao: 'Recepção',
      profissional: 'Profissional',
      paciente: 'Paciente'
    };
    return labels[perfil] || 'Usuário';
  };

  // Carregar Equipe Ativa (para Recepção)
  const carregarEquipeAtiva = async () => {
    if (loadingEquipe || equipeAtiva) return; // Evitar reload desnecessário
    setLoadingEquipe(true);
    try {
      const res = await authFetch('/inteligencia/profissionais-ativos');
      setEquipeAtiva(res.data);
    } catch (err) {
      console.error('Erro ao carregar equipe ativa:', err);
    } finally {
      setLoadingEquipe(false);
    }
  };

  // Carregar Cobertura de Push (Admin/Recepção)
  const carregarCoberturaPush = async () => {
    if (coberturaPush) return;
    try {
      const res = await authFetch('/push/cobertura-profissionais');
      setCoberturaPush(res.data);
    } catch (err) {
      console.error('Erro ao carregar cobertura push:', err);
    }
  };

  // Toggle do card de Equipe Ativa
  const toggleEquipeAtiva = () => {
    if (!equipeAtivaExpanded && !equipeAtiva) {
      carregarEquipeAtiva();
    }
    setEquipeAtivaExpanded(!equipeAtivaExpanded);
  };

  const getStatusBadge = (status) => {
    const config = {
      agendado: { label: 'Agendado', variant: 'outline', color: '#5C6B64' },
      aguardando: { label: 'Aguardando', variant: 'secondary', color: '#D4A373' },
      em_atendimento: { label: 'Em Atendimento', variant: 'default', color: '#4A6B5D' },
      finalizado: { label: 'Finalizado', variant: 'outline', color: '#7B9E87' },
      cancelado: { label: 'Cancelado', variant: 'destructive', color: '#B75D69' },
      falta: { label: 'Falta', variant: 'destructive', color: '#B75D69' }
    };
    const c = config[status] || config.agendado;
    return (
      <Badge 
        variant={c.variant}
        style={{ backgroundColor: c.variant === 'default' ? c.color : undefined }}
      >
        {c.label}
      </Badge>
    );
  };

  // Mostrar loading enquanto carrega dados (ou enquanto Admin aguarda redirecionamento)
  if (loading || user?.perfil === 'administrador') {
    return (
      <div className="space-y-8" data-testid="dashboard-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Modal bloqueante de contrato pendente (apenas profissional) */}
      <ContratoPendenteModal />
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold"
          style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
        >
          Olá, {user?.nome || 'Usuário'}!
        </h1>
        <p 
          className="mt-2 text-base"
          style={{ color: '#5C6B64', fontFamily: 'Figtree, sans-serif' }}
        >
          {getPerfilLabel(user?.perfil)} • {format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR })}
        </p>
      </div>

      {/* Stats Cards - Admin */}
      {isAdmin() && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <StatCard 
              title="Total de Pacientes" 
              value={stats?.total_pacientes || 0}
              icon={Users}
              colorKey="coral"
              onClick={() => navigate('/dashboard/pacientes')}
            />
            <StatCard 
              title="Profissionais Ativos" 
              value={stats?.total_profissionais || 0}
              icon={Stethoscope}
              colorKey="green"
              onClick={() => navigate('/dashboard/profissionais')}
            />
            <StatCard 
              title="Agendamentos Hoje" 
              value={stats?.agendamentos_hoje || 0}
              icon={Calendar}
              colorKey="blue"
              onClick={() => navigate('/dashboard/agendamentos')}
            />
            <StatCard 
              title="Receita do Mês" 
              value={`R$ ${(stats?.receita_mes || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
              icon={DollarSign}
              colorKey="yellow"
              onClick={() => navigate('/dashboard/financeiro')}
            />
          </div>
          
          {/* Card de Novas Mensagens - Admin */}
          {mensagensNaoLidas > 0 && (
            <div 
              className="rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ 
                backgroundColor: BRAND_COLORS.yellow.bg, 
                border: `1px solid ${BRAND_COLORS.yellow.border}` 
              }}
              onClick={() => navigate('/dashboard/mural')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-2.5 rounded-xl"
                    style={{ backgroundColor: BRAND_COLORS.yellow.bgHover }}
                  >
                    <MessageSquare className="h-5 w-5" style={{ color: BRAND_COLORS.yellow.text }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: BRAND_COLORS.yellow.text }}>
                      {mensagensNaoLidas} nova{mensagensNaoLidas > 1 ? 's' : ''} mensagem{mensagensNaoLidas > 1 ? 'ns' : ''} de profissionais
                    </p>
                    <p className="text-xs" style={{ color: BRAND_COLORS.yellow.text, opacity: 0.7 }}>
                      Clique para ver no Mural
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5" style={{ color: BRAND_COLORS.yellow.text }} />
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats Cards - Recepção */}
      {isRecepcao() && (
        <div className="space-y-5">
          {/* Status do Dia - 2 cards principais */}
          <div className="grid grid-cols-2 gap-4">
            <div 
              className="p-4 rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ 
                backgroundColor: BRAND_COLORS.yellow.bg, 
                border: `1px solid ${BRAND_COLORS.yellow.border}`,
                backdropFilter: 'blur(8px)'
              }}
              onClick={() => openStatusDetails('aguardando', 'Pacientes Aguardando')}
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4" style={{ color: BRAND_COLORS.yellow.text }} />
              </div>
              <p className="text-2xl font-semibold" style={{ color: BRAND_COLORS.yellow.text, fontFamily: 'Outfit, sans-serif' }}>
                {stats?.agendamentos_aguardando || 0}
              </p>
              <p className="text-xs" style={{ color: BRAND_COLORS.yellow.text, opacity: 0.8 }}>Aguardando</p>
            </div>
            
            <div 
              className="p-4 rounded-2xl cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ 
                backgroundColor: BRAND_COLORS.green.bg, 
                border: `1px solid ${BRAND_COLORS.green.border}`,
                backdropFilter: 'blur(8px)'
              }}
              onClick={() => openStatusDetails('em_atendimento', 'Pacientes em Atendimento')}
            >
              <div className="flex items-center gap-2 mb-2">
                <PlayCircle className="h-4 w-4" style={{ color: BRAND_COLORS.green.text }} />
              </div>
              <p className="text-2xl font-semibold" style={{ color: BRAND_COLORS.green.text, fontFamily: 'Outfit, sans-serif' }}>
                {stats?.agendamentos_em_atendimento || 0}
              </p>
              <p className="text-xs" style={{ color: BRAND_COLORS.green.text, opacity: 0.8 }}>Em Atend.</p>
            </div>
          </div>
          
          {/* Segunda linha: Pacientes restantes e Terapeutas atendendo */}
          <div className="grid grid-cols-2 gap-4">
            <div 
              className="rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.7)', 
                border: `1px solid ${BRAND_COLORS.pearl.border}`,
                backdropFilter: 'blur(8px)'
              }}
              onClick={openPacientesRestantes}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#71717A' }}>Pacientes restantes</p>
                  <p className="text-3xl font-semibold" style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}>
                    {stats?.pacientes_restantes_dia || 0}
                  </p>
                  <p className="text-xs" style={{ color: '#A1A1AA' }}>hoje</p>
                </div>
                <div 
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: BRAND_COLORS.blue.bg }}
                >
                  <Users className="h-6 w-6" style={{ color: BRAND_COLORS.blue.text }} />
                </div>
              </div>
            </div>
            
            <div 
              className="rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.7)', 
                border: `1px solid ${BRAND_COLORS.pearl.border}`,
                backdropFilter: 'blur(8px)'
              }}
              onClick={openTerapeutasDetails}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#71717A' }}>Terapeutas hoje</p>
                  <p className="text-3xl font-semibold" style={{ color: '#18181B', fontFamily: 'Outfit, sans-serif' }}>
                    {stats?.profissionais_ativos_dia || 0}
                  </p>
                  <p className="text-xs" style={{ color: '#A1A1AA' }}>hoje</p>
                </div>
                <div 
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: BRAND_COLORS.green.bg }}
                >
                  <Stethoscope className="h-6 w-6" style={{ color: BRAND_COLORS.green.text }} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Card de Novas Mensagens */}
          {mensagensNaoLidas > 0 && (
            <div 
              className="rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md animate-pulse"
              style={{ 
                backgroundColor: BRAND_COLORS.yellow.bg, 
                border: `1px solid ${BRAND_COLORS.yellow.border}` 
              }}
              onClick={() => navigate('/dashboard/mural')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-2.5 rounded-xl"
                    style={{ backgroundColor: BRAND_COLORS.yellow.bgHover }}
                  >
                    <MessageSquare className="h-5 w-5" style={{ color: BRAND_COLORS.yellow.text }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: BRAND_COLORS.yellow.text }}>
                      {mensagensNaoLidas} nova{mensagensNaoLidas > 1 ? 's' : ''} mensagem{mensagensNaoLidas > 1 ? 'ns' : ''}
                    </p>
                    <p className="text-xs" style={{ color: BRAND_COLORS.yellow.text, opacity: 0.7 }}>
                      Clique para ver no Mural
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5" style={{ color: BRAND_COLORS.yellow.text }} />
              </div>
            </div>
          )}
          
          {/* Card Colapsável — Cobertura de Notificações Push da Equipe (Admin/Recepção)
              Motivo: alguns profissionais reportavam não receber avisos quando a
              Recepção marca paciente como "aguardando". Investigação mostrou que
              o problema não era o backend — era que muitos profs nunca ativaram
              push notifications no navegador. Este card lista quem está sem, para
              a coordenação cobrar a ativação. */}
          <Card
            className="border shadow-sm overflow-hidden"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
            data-testid="card-cobertura-push"
          >
            <div
              className="px-4 py-3 cursor-pointer hover:bg-[#F3F0EA] transition-colors flex items-center justify-between"
              onClick={() => {
                if (!coberturaPushExpanded && !coberturaPush) {
                  carregarCoberturaPush();
                }
                setCoberturaPushExpanded(!coberturaPushExpanded);
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-xl"
                  style={{
                    backgroundColor: (coberturaPush && coberturaPush.sem_push_ativo > 0) ? '#FEF3C7' : BRAND_COLORS.green.bg,
                  }}
                >
                  <Bell className="h-5 w-5" style={{
                    color: (coberturaPush && coberturaPush.sem_push_ativo > 0) ? '#D97706' : BRAND_COLORS.green.text,
                  }} />
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#2C3632' }}>
                    Cobertura de Notificações
                  </p>
                  <p className="text-xs" style={{ color: '#5C6B64' }}>
                    {coberturaPush
                      ? `${coberturaPush.com_push_ativo} de ${coberturaPush.total_profissionais_ativos} profs ativos recebem alertas`
                      : 'Clique para verificar quem está sem notificações'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {coberturaPush && coberturaPush.sem_push_ativo > 0 && (
                  <Badge style={{ backgroundColor: '#D97706', color: '#FFFFFF' }} data-testid="badge-sem-push">
                    {coberturaPush.sem_push_ativo} sem
                  </Badge>
                )}
                {coberturaPushExpanded ? (
                  <ChevronUp className="h-5 w-5" style={{ color: '#A3B19B' }} />
                ) : (
                  <ChevronDown className="h-5 w-5" style={{ color: '#A3B19B' }} />
                )}
              </div>
            </div>
            {coberturaPushExpanded && (
              <div className="border-t px-4 py-3" style={{ borderColor: '#EAE6DF' }}>
                {!coberturaPush ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin" style={{ color: BRAND_COLORS.green.solid }} />
                  </div>
                ) : coberturaPush.sem_push_ativo === 0 ? (
                  <div className="py-4 text-center" style={{ color: '#5C6B64' }}>
                    <p className="text-sm">Todos os profissionais ativos têm notificações ativadas.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs" style={{ color: '#5C6B64' }}>
                      Os profissionais abaixo <strong>não</strong> receberão avisos por push quando a Recepção marcar paciente como &quot;aguardando&quot;.
                      Peça para eles abrirem o app em Configurações → Notificações ou pelo próprio dashboard, e clicar em &quot;Ativar agora&quot;.
                    </p>
                    <div className="max-h-72 overflow-y-auto rounded-lg border divide-y" style={{ borderColor: '#EAE6DF' }}>
                      {coberturaPush.sem_push.map((p) => (
                        <div key={p.usuario_id} className="px-3 py-2 hover:bg-[#F9F7F4]" data-testid={`sem-push-${p.usuario_id}`}>
                          <p className="text-sm font-medium" style={{ color: '#2C3632' }}>{p.nome}</p>
                          <p className="text-xs" style={{ color: '#5C6B64' }}>
                            {p.email} • {(p.especialidades || []).join(' + ') || 'Sem especialidade'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Card Colapsável - Equipe Ativa */}
          <Card 
            className="border shadow-sm overflow-hidden"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
            data-testid="card-equipe-ativa"
          >
            <div 
              className="px-4 py-3 cursor-pointer hover:bg-[#F3F0EA] transition-colors flex items-center justify-between"
              onClick={toggleEquipeAtiva}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-xl"
                  style={{ backgroundColor: BRAND_COLORS.green.bg }}
                >
                  <Users className="h-5 w-5" style={{ color: BRAND_COLORS.green.text }} />
                </div>
                <div>
                  <p className="font-semibold" style={{ color: '#2C3632' }}>
                    Equipe Ativa
                  </p>
                  <p className="text-xs" style={{ color: '#5C6B64' }}>
                    {equipeAtiva ? `${equipeAtiva.total} profissionais` : 'Clique para ver'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {equipeAtiva && (
                  <Badge style={{ backgroundColor: BRAND_COLORS.green.solid, color: '#FFFFFF' }}>
                    {equipeAtiva.total}
                  </Badge>
                )}
                {equipeAtivaExpanded ? (
                  <ChevronUp className="h-5 w-5" style={{ color: '#A3B19B' }} />
                ) : (
                  <ChevronDown className="h-5 w-5" style={{ color: '#A3B19B' }} />
                )}
              </div>
            </div>
            
            {/* Conteúdo expandido */}
            {equipeAtivaExpanded && (
              <div className="border-t" style={{ borderColor: '#EAE6DF' }}>
                {loadingEquipe ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" style={{ color: BRAND_COLORS.green.solid }} />
                    <span className="ml-2 text-sm" style={{ color: '#5C6B64' }}>Carregando equipe...</span>
                  </div>
                ) : equipeAtiva ? (
                  <>
                    {/* Campo de busca */}
                    <div className="px-3 py-2 border-b" style={{ borderColor: '#EAE6DF', backgroundColor: '#F9F7F4' }}>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: '#A3B19B' }} />
                        <input
                          type="text"
                          placeholder="Buscar profissional..."
                          value={buscaEquipe}
                          onChange={(e) => setBuscaEquipe(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2"
                          style={{ 
                            borderColor: '#EAE6DF', 
                            backgroundColor: '#FFFFFF',
                            color: '#2C3632'
                          }}
                        />
                        {buscaEquipe && (
                          <button
                            onClick={() => setBuscaEquipe('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            <X className="h-4 w-4" style={{ color: '#A3B19B' }} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Lista filtrada */}
                    <div className="max-h-[350px] overflow-y-auto">
                      {equipeAtiva.grupos
                        .map((grupo) => {
                          // Filtrar profissionais pelo termo de busca
                          const profsFiltrados = grupo.profissionais.filter((prof) => {
                            if (!buscaEquipe.trim()) return true;
                            const termo = buscaEquipe.toLowerCase();
                            return (
                              prof.nome?.toLowerCase().includes(termo) ||
                              prof.registro_profissional?.toLowerCase().includes(termo) ||
                              grupo.especialidade?.toLowerCase().includes(termo)
                            );
                          });
                          
                          // Retornar null se não houver profissionais após filtro
                          if (profsFiltrados.length === 0) return null;
                          
                          return (
                            <div key={grupo.especialidade} className="border-b last:border-b-0" style={{ borderColor: '#EAE6DF' }}>
                              <div 
                                className="px-4 py-2 flex items-center justify-between"
                                style={{ backgroundColor: BRAND_COLORS.yellow.bg }}
                              >
                                <div className="flex items-center gap-2">
                                  <Stethoscope className="h-4 w-4" style={{ color: BRAND_COLORS.yellow.text }} />
                                  <span className="font-medium text-sm" style={{ color: BRAND_COLORS.yellow.text }}>
                                    {grupo.especialidade}
                                  </span>
                                </div>
                                <Badge 
                                  variant="secondary"
                                  style={{ backgroundColor: BRAND_COLORS.yellow.solid, color: '#FFFFFF', fontSize: '0.7rem' }}
                                >
                                  {profsFiltrados.length}
                                </Badge>
                              </div>
                              <div className="divide-y" style={{ borderColor: '#F3F0EA' }}>
                                {profsFiltrados.map((prof, idx) => {
                                  const expanded = profConveniosExpandidos.has(prof.id);
                                  const convs = prof.convenios || [];
                                  return (
                                  <div key={idx} className="hover:bg-[#F9F7F4] transition-colors">
                                    <button
                                      type="button"
                                      className="w-full px-4 py-2 text-left flex items-start gap-2"
                                      onClick={() => toggleConveniosProf(prof.id)}
                                      data-testid={`prof-toggle-convenios-${prof.id}`}
                                      aria-expanded={expanded}
                                      title={expanded ? 'Ocultar convênios' : 'Ver convênios cadastrados'}
                                    >
                                      <ChevronRight
                                        className="h-4 w-4 mt-0.5 flex-shrink-0 transition-transform"
                                        style={{
                                          color: '#8A9088',
                                          transform: expanded ? 'rotate(90deg)' : 'none'
                                        }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm" style={{ color: '#2C3632' }}>
                                          {prof.nome}
                                        </p>
                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs" style={{ color: '#5C6B64' }}>
                                          {prof.registro_profissional && (
                                            <span>Reg: {prof.registro_profissional}</span>
                                          )}
                                          {prof.modelo_contrato && (
                                            <Badge
                                              variant="outline"
                                              className="text-xs py-0 px-1.5"
                                              style={{
                                                borderColor: prof.modelo_contrato === 'sublocacao' ? BRAND_COLORS.blue.border : BRAND_COLORS.coral.border,
                                                color: prof.modelo_contrato === 'sublocacao' ? BRAND_COLORS.blue.text : BRAND_COLORS.coral.text,
                                                backgroundColor: prof.modelo_contrato === 'sublocacao' ? BRAND_COLORS.blue.bg : BRAND_COLORS.coral.bg
                                              }}
                                            >
                                              {prof.modelo_contrato === 'sublocacao' ? 'Sublocação' : 'Percentual'}
                                            </Badge>
                                          )}
                                          {/* Valor da consulta particular — só aparece quando informado */}
                                          {prof.valor_consulta_particular != null && Number(prof.valor_consulta_particular) > 0 && (
                                            <span
                                              className="inline-flex items-center gap-1 font-semibold"
                                              style={{ color: '#4A6B5D' }}
                                              data-testid={`valor-particular-${prof.id}`}
                                              title="Valor da consulta particular (para informar ao paciente)"
                                            >
                                              Particular: {Number(prof.valor_consulta_particular).toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                              })}
                                            </span>
                                          )}
                                          <span
                                            className="inline-flex items-center gap-0.5 text-xs"
                                            style={{ color: '#8A9088' }}
                                          >
                                            {convs.length} convênio{convs.length === 1 ? '' : 's'}
                                          </span>
                                        </div>
                                      </div>
                                    </button>
                                    {expanded && (
                                      <div
                                        className="px-11 pb-3 pt-1"
                                        data-testid={`prof-convenios-list-${prof.id}`}
                                      >
                                        {convs.length === 0 ? (
                                          <p className="text-xs italic" style={{ color: '#B8BEB9' }}>
                                            Nenhum convênio cadastrado para este profissional.
                                          </p>
                                        ) : (
                                          <div className="flex flex-wrap gap-1.5">
                                            {convs.map((c) => (
                                              <Badge
                                                key={c.id}
                                                variant="outline"
                                                className="text-xs py-0.5 px-2"
                                                style={{
                                                  borderColor: c.ativo ? '#C6D9CE' : '#E1D1CE',
                                                  color: c.ativo ? '#4A6B5D' : '#A17870',
                                                  backgroundColor: c.ativo ? '#F0F7F4' : '#FBEEEA',
                                                }}
                                                title={c.ativo ? 'Convênio ativo' : 'Convênio inativo'}
                                              >
                                                {c.nome}
                                                {!c.ativo && ' (inativo)'}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })
                        .filter(Boolean)}
                      
                      {/* Mensagem se nenhum resultado */}
                      {buscaEquipe && equipeAtiva.grupos.every((g) => 
                        g.profissionais.every((p) => {
                          const termo = buscaEquipe.toLowerCase();
                          return !(
                            p.nome?.toLowerCase().includes(termo) ||
                            p.registro_profissional?.toLowerCase().includes(termo) ||
                            g.especialidade?.toLowerCase().includes(termo)
                          );
                        })
                      ) && (
                        <div className="py-6 text-center" style={{ color: '#5C6B64' }}>
                          <Search className="h-6 w-6 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Nenhum profissional encontrado para &ldquo;{buscaEquipe}&rdquo;</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="py-8 text-center" style={{ color: '#5C6B64' }}>
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhum profissional encontrado</p>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Stats Cards - Profissional */}
      {isProfissional() && (
        <div className="space-y-4">
          {/* AVISO — Prazo de Regularização até 20/08/2026 (tom acolhedor, sem contador) */}
          {(() => {
            const prazo = new Date('2026-08-20T23:59:59-03:00');
            const agora = new Date();
            if (agora > prazo) return null;
            return (
              <Card
                className="border shadow-sm"
                style={{ backgroundColor: '#FFF7ED', borderColor: '#F59E0B', borderRadius: '1rem' }}
                data-testid="banner-prazo-dashboard"
              >
                <CardContent className="py-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#FEF3C7' }}>
                        <AlertCircle className="h-5 w-5" style={{ color: '#D97706' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm" style={{ color: '#92400E' }}>
                          Organização do fluxo de atendimentos — a partir de 20/08/2026
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#78350F' }}>
                          Nesta data, agendamentos com status parado e sessões sem evolução passarão a limitar
                          temporariamente o acesso à agenda enquanto não forem resolvidos. Aproveite estes dias
                          para regularizar com tranquilidade.
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate('/dashboard/minhas-pendencias')}
                      style={{ backgroundColor: '#D97706', color: '#FFFFFF' }}
                      data-testid="btn-ir-minhas-pendencias"
                    >
                      Ir para Minhas Pendências
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* ALERTA: Notificações Push não ativas — mostra somente para o próprio Profissional
              quando a subscription está inativa. Casos:
              - iOS sem PWA instalado: instrui a instalar
              - Permissão 'denied' (bloqueada no navegador): pede para desbloquear
              - Padrão (default) ou revogada: botão "Ativar agora" */}
          {pushSupported && !pushSubscribed && (
            <Card
              className="border shadow-sm"
              style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B', borderRadius: '1rem' }}
              data-testid="alerta-push-inativo"
            >
              <CardContent className="py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full" style={{ backgroundColor: '#FDE68A' }}>
                      <AlertCircle className="h-5 w-5" style={{ color: '#D97706' }} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: '#92400E' }}>
                        Notificações push desativadas
                      </p>
                      <p className="text-xs" style={{ color: '#B45309' }}>
                        {pushIsIOS && !pushIsStandalone
                          ? 'No iPhone/iPad você precisa instalar o app na Tela Inicial (Safari → Compartilhar → Adicionar à Tela de Início) para receber avisos quando um paciente chegar.'
                          : pushPermission === 'denied'
                          ? 'Suas notificações estão bloqueadas no navegador. Vá em Configurações do site → Notificações e permita para receber avisos de pacientes aguardando.'
                          : 'Ative para receber um alerta quando a Recepção marcar um paciente como "aguardando".'}
                      </p>
                    </div>
                  </div>
                  {pushPermission !== 'denied' && !(pushIsIOS && !pushIsStandalone) && (
                    <Button
                      onClick={togglePushSubscription}
                      disabled={pushLoading}
                      size="sm"
                      style={{ backgroundColor: '#D97706', color: '#FFFFFF' }}
                      data-testid="btn-ativar-push"
                    >
                      {pushLoading ? 'Ativando...' : 'Ativar agora'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ALERTA: Pacientes Aguardando */}
          {pacientesAguardando.length > 0 && (
            <Card 
              className="border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md animate-pulse"
              style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B', borderRadius: '1rem' }}
              data-testid="card-pacientes-aguardando"
              onClick={() => {
                setDetailsModal({
                  open: true,
                  type: 'aguardando',
                  title: 'Pacientes Aguardando Atendimento',
                  data: pacientesAguardando
                });
              }}
            >
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full" style={{ backgroundColor: '#FDE68A' }}>
                      <AlertCircle className="h-5 w-5" style={{ color: '#D97706' }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#92400E' }}>
                        {pacientesAguardando.length} paciente{pacientesAguardando.length > 1 ? 's' : ''} aguardando!
                      </p>
                      <p className="text-xs" style={{ color: '#B45309' }}>
                        {pacientesAguardando.map(p => p.pacientes?.nome_completo?.split(' ')[0]).join(', ')}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5" style={{ color: '#D97706' }} />
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* ALERTA: Status Incoerente (vermelho) */}
          {alertasStatusIncoerente.total > 0 && (
            <Card 
              className="border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ backgroundColor: '#FEE2E2', borderColor: '#EF4444', borderRadius: '1rem' }}
              data-testid="card-status-incoerente"
              onClick={() => navigate('/dashboard/minhas-pendencias')}
            >
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full" style={{ backgroundColor: '#FECACA' }}>
                      <AlertTriangle className="h-5 w-5" style={{ color: '#DC2626' }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#7F1D1D' }}>
                        {alertasStatusIncoerente.total} paciente{alertasStatusIncoerente.total > 1 ? 's' : ''} com status pendente
                      </p>
                      <p className="text-xs" style={{ color: '#991B1B' }}>
                        Atendimentos iniciados sem finalização
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5" style={{ color: '#DC2626' }} />
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* ALERTA: Sem Evolução (vermelho) */}
          {alertasSemEvolucao.total > 0 && (
            <Card 
              className="border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ backgroundColor: '#FEE2E2', borderColor: '#EF4444', borderRadius: '1rem' }}
              data-testid="card-sem-evolucao"
              onClick={() => navigate('/dashboard/minhas-pendencias')}
            >
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full" style={{ backgroundColor: '#FECACA' }}>
                      <FileWarning className="h-5 w-5" style={{ color: '#DC2626' }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#7F1D1D' }}>
                        {alertasSemEvolucao.total} paciente{alertasSemEvolucao.total > 1 ? 's' : ''} sem evolução
                      </p>
                      <p className="text-xs" style={{ color: '#991B1B' }}>
                        Atendimentos finalizados sem registro
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5" style={{ color: '#DC2626' }} />
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Card 1: Próximo Atendimento */}
          {stats?.proximo_agendamento ? (
            <Card 
              className="border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
              onClick={() => navigate('/dashboard/minha-agenda')}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C6B64' }}>
                    Próximo Atendimento
                  </CardTitle>
                  <ChevronRight className="h-4 w-4" style={{ color: '#A3B19B' }} />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold" style={{ color: '#2C3632' }}>
                  {stats.proximo_agendamento.pacientes?.nome_completo}
                </p>
                <p className="text-sm" style={{ color: '#5C6B64' }}>
                  {format(parseLocalDateTime(stats.proximo_agendamento.data_hora_inicio), "EEEE, d/M 'às' HH:mm", { locale: ptBR })}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card 
              className="border shadow-sm"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
            >
              <CardContent className="py-6 text-center">
                <CalendarIcon className="h-8 w-8 mx-auto mb-2" style={{ color: '#A3B19B' }} />
                <p className="text-sm" style={{ color: '#5C6B64' }}>Nenhum atendimento agendado</p>
              </CardContent>
            </Card>
          )}
          
          {/* Card 2: Prontuários */}
          <Card 
            className="border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
            onClick={() => navigate('/dashboard/prontuarios')}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F0EB' }}>
                    <FileText className="h-5 w-5" style={{ color: '#4A6B5D' }} />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold" style={{ color: '#2C3632' }}>
                      Prontuários
                    </CardTitle>
                    <p className="text-xs" style={{ color: '#5C6B64' }}>
                      Evoluções e registros clínicos
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5" style={{ color: '#A3B19B' }} />
              </div>
            </CardHeader>
          </Card>
          
          {/* Cards 3 e 4: Mini estatísticas */}
          <div className="grid grid-cols-2 gap-4">
            <div 
              className="p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
              onClick={() => navigate('/dashboard/minha-agenda')}
            >
              <div className="flex items-center gap-2 mb-2">
                <CalendarDays className="h-4 w-4" style={{ color: '#4A6B5D' }} />
                <span className="text-xs font-medium" style={{ color: '#5C6B64' }}>Restantes no mês</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#2C3632' }}>
                {stats?.agendamentos_restantes_mes || 0}
              </p>
              <p className="text-xs" style={{ color: '#A3B19B' }}>agendamentos</p>
            </div>
            
            <div 
              className="p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
              data-testid="card-faltas-cancelamentos"
              onClick={() => {
                if (faltasCancelamentos.length > 0) {
                  setDetailsModal({
                    open: true,
                    type: 'faltas_cancelamentos',
                    title: 'Faltas e Cancelamentos do Mês',
                    data: faltasCancelamentos
                  });
                }
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <UserX className="h-4 w-4" style={{ color: '#B75D69' }} />
                <span className="text-xs font-medium" style={{ color: '#5C6B64' }}>Faltas/Cancel.</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: '#2C3632' }}>
                {stats?.faltas_cancelamentos_mes || 0}
              </p>
              <p className="text-xs" style={{ color: '#A3B19B' }}>neste mês</p>
            </div>
          </div>
        </div>
      )}

      {/* Status do Dia - Admin only */}
      {isAdmin() && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatusMiniCard 
            label="Agendados" 
            value={stats?.agendamentos_agendado || 0}
            icon={Clock}
            color="#5C6B64"
            onClick={() => navigate('/dashboard/agendamentos')}
          />
          <StatusMiniCard 
            label="Aguardando" 
            value={stats?.agendamentos_aguardando || 0}
            icon={AlertCircle}
            color="#D4A373"
            onClick={() => navigate('/dashboard/agendamentos')}
          />
          <StatusMiniCard 
            label="Em Atendimento" 
            value={stats?.agendamentos_em_atendimento || 0}
            icon={PlayCircle}
            color="#4A6B5D"
            onClick={() => navigate('/dashboard/agendamentos')}
          />
          <StatusMiniCard 
            label="Finalizados" 
            value={stats?.agendamentos_finalizado || 0}
            icon={CheckCircle}
            color="#7B9E87"
            onClick={() => navigate('/dashboard/agendamentos')}
          />
        </div>
      )}

      {/* Lista de Agendamentos do Dia - Apenas para Admin/Recepção */}
      {isAdminOrRecepcao() && (
        <Card 
          className="border shadow-sm"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF', borderRadius: '1rem' }}
        >
          <CardHeader 
            className="cursor-pointer hover:bg-[#F3F0EA] transition-colors rounded-t-2xl"
            onClick={() => navigate('/dashboard/agendamentos')}
          >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle 
                className="text-lg font-semibold flex items-center gap-2"
                style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
              >
                <CalendarIcon className="h-5 w-5" style={{ color: '#4A6B5D' }} />
                Agenda de Hoje
              </CardTitle>
              <CardDescription style={{ color: '#5C6B64' }}>
                {agendamentosHoje.length} atendimento{agendamentosHoje.length !== 1 ? 's' : ''} programado{agendamentosHoje.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <ChevronRight className="h-5 w-5" style={{ color: '#A3B19B' }} />
          </div>
        </CardHeader>
        <CardContent>
          {agendamentosHoje.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4" style={{ color: '#EAE6DF' }} />
              <p style={{ color: '#5C6B64' }}>Nenhum agendamento para hoje</p>
            </div>
          ) : (
            <div className="space-y-3">
              {agendamentosHoje.map((ag) => (
                <div 
                  key={ag.id}
                  className="flex items-center justify-between p-4 rounded-xl transition-colors hover:bg-[#F3F0EA]"
                  style={{ backgroundColor: '#FDFBF7' }}
                  data-testid={`agendamento-${ag.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="text-center min-w-[60px] p-2 rounded-lg"
                      style={{ backgroundColor: '#E8F0EB' }}
                    >
                      <p className="text-lg font-bold" style={{ color: '#4A6B5D' }}>
                        {format(parseLocalDateTime(ag.data_hora_inicio), 'HH:mm')}
                      </p>
                      <p className="text-xs" style={{ color: '#5C6B64' }}>
                        {format(parseLocalDateTime(ag.data_hora_fim), 'HH:mm')}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium" style={{ color: '#2C3632' }}>
                        {ag.pacientes?.nome_completo || 'Paciente'}
                      </p>
                      <p className="text-sm" style={{ color: '#5C6B64' }}>
                        {ag.profissionais?.usuarios?.nome || 'Profissional'} 
                        {ag.salas && ` • Sala ${ag.salas.numero}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {ag.convenios?.nome && (
                      <Badge variant="outline" style={{ borderColor: '#A3B19B', color: '#5C6B64' }}>
                        {ag.convenios.nome}
                      </Badge>
                    )}
                    {getStatusBadge(ag.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Modal de Detalhes */}
      <Dialog open={detailsModal.open} onOpenChange={(open) => setDetailsModal({ ...detailsModal, open })}>
        <DialogContent className="max-w-md max-h-[80vh]" style={{ backgroundColor: '#FDFBF7' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}>
              {detailsModal.title}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            {detailsModal.data.length === 0 ? (
              <div className="text-center py-8">
                <User className="h-12 w-12 mx-auto mb-4" style={{ color: '#EAE6DF' }} />
                <p style={{ color: '#5C6B64' }}>Nenhum registro encontrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Renderização condicional baseada no tipo */}
                {detailsModal.type === 'terapeutas' && detailsModal.data.map((terapeuta, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-xl border flex items-center gap-4"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
                  >
                    <div className="p-3 rounded-full" style={{ backgroundColor: '#E8F0EB' }}>
                      <Stethoscope className="h-5 w-5" style={{ color: '#4A6B5D' }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-base" style={{ color: '#2C3632' }}>{terapeuta.nome}</p>
                      {terapeuta.especialidade && (
                        <p className="text-xs" style={{ color: '#5C6B64' }}>{terapeuta.especialidade}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: '#4A6B5D' }}>
                        {terapeuta.agendamentos.length}
                      </p>
                      <p className="text-xs" style={{ color: '#5C6B64' }}>
                        atendimento{terapeuta.agendamentos.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Renderização para Pacientes Aguardando */}
                {detailsModal.type === 'aguardando' && detailsModal.data.map((ag) => (
                  <div 
                    key={ag.id}
                    className="p-4 rounded-xl border"
                    style={{ backgroundColor: '#FFFBEB', borderColor: '#F59E0B' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="text-center min-w-[60px] p-2 rounded-lg"
                          style={{ backgroundColor: '#FDE68A' }}
                        >
                          <p className="text-lg font-bold" style={{ color: '#D97706' }}>
                            {format(parseLocalDateTime(ag.data_hora_inicio), 'HH:mm')}
                          </p>
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: '#92400E' }}>
                            {ag.pacientes?.nome_completo || 'Paciente'}
                          </p>
                          <p className="text-xs" style={{ color: '#B45309' }}>
                            Agendado para {formatDateTime(ag.data_hora_inicio, "HH:mm")} - {formatDateTime(ag.data_hora_fim, "HH:mm")}
                          </p>
                          {ag.salas?.nome && (
                            <Badge 
                              variant="outline" 
                              className="text-xs mt-1"
                              style={{ borderColor: '#F59E0B', color: '#D97706', backgroundColor: '#FEF3C7' }}
                            >
                              {ag.salas.nome}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge 
                        className="text-xs animate-pulse"
                        style={{ backgroundColor: '#F59E0B', color: 'white' }}
                      >
                        Aguardando
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        className="flex-1 gap-2"
                        style={{ backgroundColor: '#4A6B5D', color: 'white' }}
                        disabled={iniciandoIds.has(ag.id)}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (iniciandoIds.has(ag.id)) return;
                          setIniciandoIds(prev => {
                            const next = new Set(prev);
                            next.add(ag.id);
                            return next;
                          });
                          try {
                            await authFetch(`/agendamentos/${ag.id}/status`, {
                              method: 'PUT',
                              data: { status: 'em_atendimento' }
                            });
                            toast.success(`Atendimento de ${ag.pacientes?.nome_completo?.split(' ')[0] || 'paciente'} iniciado!`);
                            setDetailsModal({ open: false, type: null, title: '', data: [] });
                            // Atualizar lista de pacientes aguardando
                            const aguardandoRes = await authFetch('/agendamentos/meus-aguardando');
                            setPacientesAguardando(aguardandoRes.data || []);
                            // Redirecionar para prontuário do paciente
                            navigate(`/dashboard/prontuarios?paciente_id=${ag.paciente_id}`);
                          } catch (err) {
                            toast.error('Erro ao iniciar atendimento');
                          } finally {
                            setIniciandoIds(prev => {
                              const next = new Set(prev);
                              next.delete(ag.id);
                              return next;
                            });
                          }
                        }}
                        data-testid={`btn-iniciar-atendimento-${ag.id}`}
                      >
                        <PlayCircle className="h-4 w-4" />
                        {iniciandoIds.has(ag.id) ? 'Iniciando…' : 'Iniciar Atendimento'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailsModal({ open: false, type: null, title: '', data: [] });
                          navigate('/dashboard/minha-agenda');
                        }}
                        style={{ borderColor: '#D97706', color: '#D97706' }}
                      >
                        Ver Agenda
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Renderização para Faltas e Cancelamentos */}
                {detailsModal.type === 'faltas_cancelamentos' && detailsModal.data.map((ag) => (
                  <div 
                    key={ag.id}
                    className="p-4 rounded-xl border"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div 
                          className="text-center min-w-[60px] p-2 rounded-lg"
                          style={{ backgroundColor: ag.status === 'falta' ? '#FEE2E2' : '#FEF3C7' }}
                        >
                          <p className="text-xs font-medium" style={{ color: ag.status === 'falta' ? '#991B1B' : '#92400E' }}>
                            {format(parseLocalDateTime(ag.data_hora_inicio), 'dd/MM')}
                          </p>
                          <p className="text-xs" style={{ color: ag.status === 'falta' ? '#B91C1C' : '#B45309' }}>
                            {format(parseLocalDateTime(ag.data_hora_inicio), 'HH:mm')}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#2C3632' }}>
                            {ag.pacientes?.nome_completo || 'Paciente'}
                          </p>
                          <Badge 
                            variant="outline" 
                            className="text-xs mt-1"
                            style={{ 
                              borderColor: ag.status === 'falta' ? '#FCA5A5' : '#FCD34D',
                              color: ag.status === 'falta' ? '#B91C1C' : '#B45309',
                              backgroundColor: ag.status === 'falta' ? '#FEF2F2' : '#FFFBEB'
                            }}
                          >
                            {ag.status === 'falta' ? 'Falta' : 'Cancelado'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    {ag.observacoes && (
                      <div className="mt-2 pl-[72px]">
                        <p className="text-xs" style={{ color: '#5C6B64' }}>
                          <span className="font-medium">Justificativa:</span> {ag.observacoes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                {(detailsModal.type === 'status' || detailsModal.type === 'pacientes') && detailsModal.data.map((ag) => (
                  <div 
                    key={ag.id}
                    className="p-4 rounded-xl border"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="text-center min-w-[50px] p-2 rounded-lg"
                          style={{ backgroundColor: '#E8F0EB' }}
                        >
                          <p className="text-sm font-bold" style={{ color: '#4A6B5D' }}>
                            {format(parseLocalDateTime(ag.data_hora_inicio), 'HH:mm')}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#2C3632' }}>
                            {ag.pacientes?.nome_completo || 'Paciente'}
                          </p>
                          <p className="text-xs" style={{ color: '#5C6B64' }}>
                            {ag.profissionais?.usuarios?.nome || 'Profissional'}
                          </p>
                        </div>
                      </div>
                      {ag.salas?.nome && (
                        <Badge variant="outline" className="text-xs" style={{ borderColor: '#A3B19B', color: '#5C6B64' }}>
                          {ag.salas.nome}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}

                {/* Status Incoerente - Lista de pacientes com atendimento pendente */}
                {detailsModal.type === 'status_incoerente' && detailsModal.data.map((paciente) => (
                  <div 
                    key={paciente.paciente_id}
                    className="p-4 rounded-xl border cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
                    onClick={() => {
                      setDetailsModal({ ...detailsModal, open: false });
                      // Navegar para a agenda na data específica do agendamento pendente
                      navigate(`/dashboard/minha-agenda?data=${paciente.data_agendamento}`);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: '#FEE2E2' }}
                        >
                          <AlertTriangle className="h-5 w-5" style={{ color: '#DC2626' }} />
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#2C3632' }}>
                            {paciente.paciente_nome}
                          </p>
                          <p className="text-xs" style={{ color: '#5C6B64' }}>
                            Data: {format(new Date(paciente.data_agendamento + 'T12:00:00'), 'dd/MM/yyyy')} • Status: {paciente.status_atual}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4" style={{ color: '#5C6B64' }} />
                    </div>
                  </div>
                ))}

                {/* Sem Evolução - Lista de pacientes sem registro */}
                {detailsModal.type === 'sem_evolucao' && detailsModal.data.map((paciente) => (
                  <div 
                    key={paciente.paciente_id}
                    className="p-4 rounded-xl border cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#EAE6DF' }}
                    onClick={() => {
                      setDetailsModal({ ...detailsModal, open: false });
                      // Navegar direto para prontuários com paciente pré-selecionado
                      navigate(`/dashboard/prontuarios?paciente_id=${paciente.paciente_id}`);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: '#FEE2E2' }}
                        >
                          <FileWarning className="h-5 w-5" style={{ color: '#DC2626' }} />
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#2C3632' }}>
                            {paciente.paciente_nome}
                          </p>
                          <p className="text-xs" style={{ color: '#5C6B64' }}>
                            {paciente.atendimentos_sem_evolucao} atendimento{paciente.atendimentos_sem_evolucao > 1 ? 's' : ''} sem evolução
                          </p>
                          {/* Mostrar datas pendentes */}
                          {paciente.datas_pendentes && paciente.datas_pendentes.length > 0 && (
                            <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#B75D69' }}>
                              <CalendarIcon className="h-3 w-3" />
                              {paciente.datas_pendentes.slice(0, 5).map(data => {
                                const [ano, mes, dia] = data.split('-');
                                return `${dia}/${mes}`;
                              }).join(', ')}
                              {paciente.datas_pendentes.length > 5 && ` +${paciente.datas_pendentes.length - 5}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4" style={{ color: '#5C6B64' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="pt-4 border-t" style={{ borderColor: '#EAE6DF' }}>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setDetailsModal({ ...detailsModal, open: false });
                if (detailsModal.type === 'sem_evolucao') {
                  navigate('/dashboard/prontuarios');
                } else if (detailsModal.type === 'status_incoerente') {
                  navigate('/dashboard/minha-agenda');
                } else {
                  navigate('/dashboard/agendamentos');
                }
              }}
              style={{ borderColor: '#4A6B5D', color: '#4A6B5D' }}
            >
              {detailsModal.type === 'sem_evolucao' ? 'Ver Prontuários' :
               detailsModal.type === 'status_incoerente' ? 'Ver Minha Agenda' :
               'Ver Agenda Completa'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal para ativar biometria */}
      <Dialog open={biometricModal} onOpenChange={setBiometricModal}>
        <DialogContent className="sm:max-w-md" style={{ borderRadius: '1rem' }}>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div 
                className="p-4 rounded-full"
                style={{ backgroundColor: `${BRAND_COLORS.green}20` }}
              >
                <Fingerprint className="h-10 w-10" style={{ color: BRAND_COLORS.green }} />
              </div>
            </div>
            <DialogTitle className="text-center text-xl" style={{ color: '#2C3632' }}>
              Ativar Login por Digital
            </DialogTitle>
            <DialogDescription className="text-center" style={{ color: '#5C6B64' }}>
              Quer fazer login mais rápido? Cadastre sua digital ou Face ID para entrar no sistema com apenas um toque!
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center gap-3 p-4 rounded-lg my-4" style={{ backgroundColor: '#F3F0EA' }}>
            <Smartphone className="h-5 w-5" style={{ color: '#5C6B64' }} />
            <div className="text-sm" style={{ color: '#5C6B64' }}>
              Funciona neste dispositivo. Você pode remover a qualquer momento nas configurações.
            </div>
          </div>
          
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="w-full gap-2"
              style={{ backgroundColor: BRAND_COLORS.green, color: '#FFFFFF' }}
              onClick={handleRegisterBiometric}
              disabled={biometricLoading}
            >
              {biometricLoading ? (
                <>Aguarde...</>
              ) : (
                <>
                  <Fingerprint className="h-4 w-4" />
                  Ativar Biometria
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={handleDismissBiometric}
            >
              Agora não
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente Mini Card de Status
const StatusMiniCard = ({ label, value, icon: Icon, color, onClick }) => (
  <div 
    className={`flex items-center gap-3 p-4 rounded-xl transition-all duration-200 hover:shadow-md ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}
    style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAE6DF' }}
    onClick={onClick}
  >
    <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <div className="flex-1">
      <p className="text-xl font-bold" style={{ color: '#2C3632' }}>{value}</p>
      <p className="text-xs" style={{ color: '#5C6B64' }}>{label}</p>
    </div>
    {onClick && <ChevronRight className="h-4 w-4" style={{ color: '#A3B19B' }} />}
  </div>
);

export default DashboardHome;
